/**
 * The frame every card of the Admin person record shares (D580): one read, four honest states.
 *
 * Each card of the 360 record reads its own route, so one missing permission or one failed call blanks THAT card only — never
 * the page. A card is therefore in exactly one of:
 *   - held back  (the read needs something the console lacks, e.g. a market) — a translated caption says what;
 *   - loading    — skeleton lines after the theme's delay;
 *   - refused    (403) — "your access does not let you see {what}", with the catalogued reason;
 *   - failed     — "could not read {what}", with the catalogued failure and a retry;
 *   - empty / ready — the card's own content.
 * Every sentence is translated and every failure goes through `useVocab().fail` — never a code, never an English fallback.
 */
import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { type Result } from '@vello/core';
import { type ApiError } from '@vello/api-client';
import { Button, RecordPanel, RecordSection, Text } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { LoadingBlock } from './loading-block';

export type CardState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'error'; readonly error: ApiError };

/**
 * One read of a card. `undefined` as `read` holds the card back (the state is then `undefined`). A late answer of an older call
 * is dropped. `reload()` reads again.
 */
export function useCardRead<T>(read: (() => Promise<Result<T, ApiError>>) | undefined, deps: readonly unknown[]): { readonly state: CardState<T> | undefined; readonly reload: () => void } {
  const [state, setState] = useState<CardState<T> | undefined>(read === undefined ? undefined : { status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const seq = useRef(0);
  const readRef = useRef(read);
  readRef.current = read;
  const enabled = read !== undefined;

  useEffect(() => {
    const fn = readRef.current;
    if (fn === undefined) {
      setState(undefined);
      return undefined;
    }
    const mine = ++seq.current;
    setState({ status: 'loading' });
    void fn().then((res) => {
      if (mine !== seq.current) return;
      setState(res.ok ? { status: 'ready', value: res.value } : { status: 'error', error: res.error });
    });
    return () => {
      seq.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the caller's list; `read` is read through a ref
  }, [enabled, attempt, ...deps]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, reload };
}

/** The sentence a card shows in place of its content when its read did not answer. */
export function CardNote({ state, what, reload }: { state: CardState<unknown>; what: string; reload?: () => void }): ReactElement | null {
  const { t } = useT();
  const { fail } = useVocab();
  if (state.status !== 'error') return null;
  const refused = state.error.status === 403 || state.error.code === 'FORBIDDEN';
  return (
    <>
      <Text as="p" role="caption" tone="secondary">
        {t(refused ? 'prec.card.refused' : 'prec.card.failed', { what, reason: fail(state.error) })}
      </Text>
      {!refused && reload !== undefined && (
        <Button variant="ghost" size="sm" onClick={reload}>
          {t('ui.retry')}
        </Button>
      )}
    </>
  );
}

export interface CardBodyProps<T> {
  readonly what: string;
  readonly state: CardState<T> | undefined;
  readonly reload?: () => void;
  /** The caption when the card is held back (state undefined). */
  readonly heldBack?: string;
  /** Whether a ready value has nothing to show, and the caption then. */
  readonly isEmpty?: (value: T) => boolean;
  readonly empty?: string;
  readonly children: (value: T) => ReactNode;
}

/** The state switch of a card (or of a section inside one): held back, loading, refused / failed, empty, or the content. */
export function CardBody<T>({ what, state, reload, heldBack, isEmpty, empty, children }: CardBodyProps<T>): ReactElement {
  if (state === undefined) {
    return (
      <Text as="p" role="caption" tone="secondary">
        {heldBack ?? ''}
      </Text>
    );
  }
  if (state.status === 'loading') return <LoadingBlock lines={2} />;
  if (state.status === 'error') return <CardNote state={state} what={what} {...(reload !== undefined ? { reload } : {})} />;
  if (isEmpty?.(state.value) === true) {
    return (
      <Text as="p" role="caption" tone="secondary">
        {empty ?? ''}
      </Text>
    );
  }
  return <>{children(state.value)}</>;
}

export interface PersonCardProps<T> extends CardBodyProps<T> {
  readonly title: string;
  readonly actions?: ReactNode;
}

export function PersonCard<T>({ title, actions, ...body }: PersonCardProps<T>): ReactElement {
  return (
    <RecordPanel title={title} {...(actions !== undefined ? { actions } : {})}>
      <CardBody {...body} />
    </RecordPanel>
  );
}

/** A titled sub-block of a card that reads on its own (the standing card has three). */
export function CardSection<T>({ title, ...body }: CardBodyProps<T> & { readonly title: string }): ReactElement {
  return (
    <RecordSection>
      <Text as="h3" role="label">
        {title}
      </Text>
      <CardBody {...body} />
    </RecordSection>
  );
}
